import * as React from 'react'

import { classList } from 'react-classlist-helper'

import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'

import { ChipMoreButton } from '../BaseChip'
import ResourceChip, { IResourceChipItem } from './ResourceChip'

// localization: translated

interface IProps {
  items: IResourceChipItem[]
  onRemove?: (id: string) => void
  onItemClick?: (id: string) => void
  onContainerClick?: () => void
  isViewMode?: boolean
  renderIcon?: (item: IResourceChipItem) => React.ReactNode
}

const PADDING_WIDTH = 16
const MORE_BUTTON_WIDTH = 94
const DEFAULT_MIN_ROWS = 3
const DEFAULT_MAX_ROWS = 100_000

const ResourceChipList: React.FC<IProps> = ({
  items,
  onRemove,
  onItemClick,
  onContainerClick,
  isViewMode,
  renderIcon,
}) => {
  const [maxRows, setMaxRows] = React.useState(DEFAULT_MIN_ROWS)

  const toggleExpansion = React.useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation()
    setMaxRows(prev =>
      prev === DEFAULT_MAX_ROWS ? DEFAULT_MIN_ROWS : DEFAULT_MAX_ROWS,
    )
  }, [])

  const isExpanded = maxRows === DEFAULT_MAX_ROWS

  const chipElements = React.useMemo(
    () =>
      items.map(item => (
        <ResourceChip
          key={item.id}
          item={item}
          onRemove={onRemove}
          onClick={onItemClick}
          isRemovable={!isViewMode && !!onRemove}
          icon={renderIcon?.(item)}
        />
      )),
    [items, onRemove, onItemClick, isViewMode, renderIcon],
  )

  return (
    <DynamicOverflowList
      shouldIgnoreScrollBarWidth
      shouldRecalculateRowsCount
      items={chipElements}
      rowsCount={maxRows}
      paddingWidth={PADDING_WIDTH}
      moreButtonWidth={MORE_BUTTON_WIDTH}
      elementsClassName="tw:align-middle tw:overflow-hidden"
    >
      {({ visibleElements, overflowElements, containerRefSetter }) => (
        <div
          ref={containerRefSetter}
          onClick={onContainerClick}
          className={classList({
            'tw:cursor-pointer tw:w-full': true,
            'tw:overflow-auto tw:max-h-[70vh]': isExpanded,
          })}
        >
          {isExpanded ? (
            <>
              {visibleElements}
              {overflowElements}
            </>
          ) : (
            visibleElements
          )}
          {!isExpanded && overflowElements.length > 0 && (
            <ChipMoreButton
              isExpanded={false}
              overflowCount={overflowElements.length}
              onClick={toggleExpansion}
            />
          )}
          {isExpanded && (
            <ChipMoreButton isExpanded={true} onClick={toggleExpansion} />
          )}
        </div>
      )}
    </DynamicOverflowList>
  )
}

export default ResourceChipList
